/**
 * The live progress line of each running direct session, by task id.
 *
 * Fed by the `direct_session.progress` browser event, which every window
 * applies through the frame reducer, so this store is not mirrored. An empty
 * line (sent when the turn ends) clears the task's entry, and so does a task
 * row whose direct session is no longer running, so a missed clear never
 * outlives the turn.
 *
 * A tool step of a turn Denker sent names its turn (`step.execution_id`); the
 * entry keeps that id until the turn's clear, so the task chat shows the
 * turn's steps as running.
 */
import { create } from "zustand";
import { asActivityLabel, type ActivityLabel } from "@/lib/activity-label";

export interface DirectSessionProgress {
  /** The line in English, or the assistant's own latest line. */
  text: string;
  /** `text` in the app language, rendered at display time; absent when
   *  `text` is the assistant's own line. */
  label?: ActivityLabel;
  /** The user's app may be waiting for an approval only it can answer. */
  waiting: boolean;
  /** The running Denker turn, once one of its steps arrived. */
  executionId?: string;
}

interface DirectSessionProgressState {
  byTask: Record<string, DirectSessionProgress>;
  apply: (data: Record<string, unknown>) => void;
}

export const useDirectSessionProgressStore =
  create<DirectSessionProgressState>()((set) => ({
    byTask: {},

    apply: (data) => {
      const taskId = data.task_id;
      if (typeof taskId !== "string" || !taskId) return;
      const text = typeof data.text === "string" ? data.text.trim() : "";
      const label = asActivityLabel(data.label);
      const step = data.step as Record<string, unknown> | undefined;
      const stepExecutionId =
        typeof step?.execution_id === "string" ? step.execution_id : undefined;
      set((state) => {
        if (!text) {
          if (!(taskId in state.byTask)) return state;
          const byTask = { ...state.byTask };
          delete byTask[taskId];
          return { byTask };
        }
        const executionId =
          stepExecutionId ?? state.byTask[taskId]?.executionId;
        return {
          byTask: {
            ...state.byTask,
            [taskId]: {
              text,
              ...(label ? { label } : {}),
              waiting: data.waiting === true,
              ...(executionId ? { executionId } : {}),
            },
          },
        };
      });
    },
  }));

/** The task's live direct-session progress line, if one is showing. */
export function useDirectSessionProgress(
  taskId: string | undefined,
): DirectSessionProgress | undefined {
  return useDirectSessionProgressStore((state) =>
    taskId ? state.byTask[taskId] : undefined,
  );
}
